import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { Button, Modal } from '@canaveral/components';
import { Tabs, Tab } from 'react-bootstrap';
import { LimitContextProvider } from '../../../../../admin/contexts/LimitContext';
import MediaGallerySelector from '../../../../../admin/containers/MediaGallerySelector';
import Media from '../../../../../modules/react/editors/ImageWidget/Media';
import Options from '../../../../../modules/react/editors/ImageWidget/Options';
import styles from './styles.scss';

const propTypes = {
  autoplay: PropTypes.bool,
  bannerText: PropTypes.string,
  data: PropTypes.shape({
    mediumUpload: PropTypes.shape({
      medium: PropTypes.object,
      userErrors: PropTypes.array,
    }).isRequired,
  }),
  otherImages: PropTypes.arrayOf(PropTypes.shape({
    mediumId: PropTypes.string,
    file: PropTypes.string,
    alt: PropTypes.string,
    title: PropTypes.string,
    link: PropTypes.string,
    target: PropTypes.string,
  })).isRequired,
  loading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  showBanner: PropTypes.bool,
  uploadedFile: PropTypes.shape({
    file: PropTypes.string.isRequired,
  }),
};

const defaultProps = {
  autoplay: undefined,
  bannerText: undefined,
  data: undefined,
  loading: undefined,
  showBanner: undefined,
  uploadedFile: undefined,
};

const Editor = ({
  autoplay: initialAutoplay,
  bannerText: initialBannerText,
  data,
  otherImages: initialImages,
  loading,
  onCancel,
  onSave,
  onUpload,
  showBanner: initialShowBanner,
  uploadedFile,
}) => {
  const [showGallery, setShowGallery] = useState(false);
  const [selectedMedium, setSelectedMedium] = useState();
  const [images, setImages] = useState(initialImages);
  // Add actual clone for starting data reference
  const [startingValues] = useState(initialImages.map(item => ({ ...item })));
  const [initialValues, setInitialValues] = useState(initialImages);
  const [error, setError] = useState();
  const [usedMedia, setUsedMedia] = useState();
  const [showBanner, setShowBanner] = useState(initialShowBanner);
  const [bannerText, setBannerText] = useState(initialBannerText);
  const [autoplay, setAutoplay] = useState(initialAutoplay);

  useEffect(() => {
    if (data && data.mediumUpload.medium) {
      setImages([...images, {
        ...data.mediumUpload.medium,
        mediumId: data.mediumUpload.medium.id,
        file: `${data.mediumUpload.medium.companyId}/${data.mediumUpload.medium.filename}`,
      }]);
    } else if (data && data.mediumUpload.userErrors.length) {
      setError(data.mediumUpload.userErrors.map(err => err.message));
    }
  }, [data]);

  useEffect(() => {
    if (uploadedFile && uploadedFile.file) {
      setImages([...images, { ...uploadedFile }]);
    }
  }, [uploadedFile]);

  useEffect(() => {
    setError();
    const media = images.filter(image => image.mediumId).map(image => image.mediumId);
    setUsedMedia(media);
  }, [images]);

  const onClickChooseImage = () => {
    setImages([...images, { ...selectedMedium, file: `${selectedMedium.companyId}/${selectedMedium.filename}` }]);
    setInitialValues(prevValues => [...prevValues, selectedMedium]);
    setSelectedMedium(null);
    setShowGallery(false);
  };

  const handleDelete = index => () => {
    const newImages = [...images];
    const removedImage = newImages.splice(index, 1);
    setImages(newImages);

    if (removedImage) {
      const modifiedIndex = initialValues.findIndex(val => val.mediumId === removedImage.mediumId);
      const newInitialValues = [...initialValues];
      if (modifiedIndex > -1) {
        newInitialValues.splice(modifiedIndex, 1); setInitialValues(newInitialValues);
      }
    }
  };

  const handleMove = (index, modifier) => () => {
    const newImages = [...images];
    newImages.splice(index + modifier, 0, newImages.splice(index, 1)[0]);
    setImages(newImages);
  };

  const changeValue = (index, valueName, value) => {
    const newImages = [...images];
    newImages[index][valueName] = value;
    setImages(newImages);

    const modifiedImage = newImages[index];
    const unmodified = initialValues.some(val => val.mediumId !== modifiedImage.mediumId);
    if (unmodified) { setInitialValues(prevValues => [...prevValues, modifiedImage]); }
  };

  const handleAltChange = index => ({ target }) => {
    changeValue(index, 'alt', target.value);
  };

  const handleDecorativeChange = index => ({ target }) => {
    changeValue(index, 'decorative', target.checked);
  };

  const handleTitleChange = index => ({ target }) => {
    changeValue(index, 'title', target.value);
  };

  const handleHrefChange = index => ({ target }) => {
    changeValue(index, 'link', target.value);
  };

  const setTarget = index => ({ target }) => {
    changeValue(index, 'target', target.value);
  };

  const handleVideoEmbedChange = index => ({ target }) => {
    changeValue(index, 'videoEmbedCode', target.value);
  };
  const onSubmit = () => {
    if (images
      .some((
        { alt, decorative, videoEmbedCode },
      ) => (!decorative && !alt) && (!alt && !videoEmbedCode))) {
      setError('All non-decorative images require an alt text.');
    } else { onSave({ images, autoplay, showBanner, bannerText, initialValues: startingValues }); }
  };
  return (
    <Modal onDismiss={onCancel} show size="xl">
      <Modal.Header>
        <Modal.Title id="image-editor-modal">
          {showGallery ? 'Media Gallery' : 'Facility Slideshow Builder'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showGallery ? (
          <LimitContextProvider>
            <MediaGallerySelector
              setSelectedMedium={setSelectedMedium}
              selectedMedium={selectedMedium}
              usedMedia={usedMedia}
              showTrash={false}
              showSearch
            />
          </LimitContextProvider>
        ) : (
          <Tabs defaultActiveKey={1} id="uncontrolled-tab-example">
            <Tab eventKey={1} title="Images & Video" className={styles.tab}>
              <Media
                error={error}
                handleAltChange={handleAltChange}
                handleDelete={handleDelete}
                handleHrefChange={handleHrefChange}
                handleMove={handleMove}
                handleTitleChange={handleTitleChange}
                handleVideoEmbedChange={handleVideoEmbedChange}
                handleDecorativeChange={handleDecorativeChange}
                videoUploadEnabled
                images={images}
                loading={loading}
                onUpload={onUpload}
                selectedMedium={selectedMedium}
                setImages={setImages}
                setSelectedMedium={setSelectedMedium}
                setShowGallery={setShowGallery}
                setTarget={setTarget}
                showGallery={showGallery}
                usedMedia={usedMedia}
                limit={10}
              />
            </Tab>
            <Tab eventKey={2} title="Options & Settings" className={styles.tab}>
              <Options
                autoplay={autoplay}
                bannerText={bannerText}
                images={images}
                imageClassname={styles.imagePreview}
                setAutoplay={setAutoplay}
                setBannerText={setBannerText}
                setShowBanner={setShowBanner}
                showBanner={showBanner}
              />
            </Tab>
          </Tabs>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          className={styles.button}
          onClick={showGallery ? () => setShowGallery(false) : onCancel}
          context="neutral"
        >
          Cancel
        </Button>
        <Button
          className={styles.button}
          context="positive"
          disabled={showGallery && !selectedMedium}
          onClick={showGallery ? onClickChooseImage : onSubmit}
        >
          {showGallery ? 'Choose Image' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

Editor.propTypes = propTypes;
Editor.defaultProps = defaultProps;

Editor.fragments = {
  media: gql`
    fragment FacilityImageSlideShowEditor on Medium {
      companyId
      filename
      trash
      url
      decorative
      formattedUpdatedAt
      defaultMetadata {
        title
        altText
      }
      metadata(locale: $locale) {
        id
        locale
        altText
        title
      }
    }
  `,
};

export default Editor;
